import { CSSProperties, ClassAttributes, useMemo } from "react";

import { UploadIcon } from "@radix-ui/react-icons";
import { Flex, Text } from "@radix-ui/themes";
import { Accept, DropzoneOptions, useDropzone } from "react-dropzone";

import { Button } from "../Button";

const baseStyle: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 10,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
  cursor: "pointer",
};

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

export type FileUploadProps = {
  onUpload: (files: File[]) => void;
  dragActiveText?: string;
  defaultText?: string;
  multiple?: boolean;
  accept?: Accept;
};

export const FileUpload = ({
  onUpload,
  dragActiveText = "Drop here!",
  defaultText = "Drag and Drop",
  multiple,
  accept = {
    "application/pdf": [".pdf"],
    "image/png": [".png"],
    "image/jpeg": [".jpeg"],
  },
}: FileUploadProps) => {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept,
    onDrop: onUpload,
    multiple,
  } as unknown as DropzoneOptions);

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const clickText = multiple ? "Choose Files" : "Choose File";
  const supportedFileTypesReadable = `Supports ${Object.values(accept)
    .flat()
    .join(", ")}`;

  return (
    <div
      {...getRootProps({
        style,
      })}
    >
      <input {...(getInputProps() as ClassAttributes<HTMLInputElement>)} />
      <Flex direction="column" gap="2" justify="center" align="center">
        <UploadIcon width="20" height="20" />
        <Button onClick={(e) => e.preventDefault()}>{clickText}</Button>
        <Text size="2" style={{ color: "var(--gray-10)" }} weight="medium">
          {isDragActive ? dragActiveText : `or ${defaultText}`}
        </Text>
        <Text size="2">{supportedFileTypesReadable}</Text>
      </Flex>
    </div>
  );
};
